import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { CCard, CCardBody, CButton, CCol, CRow } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import CFormGroup from '../../../../../migration/CFormGroup';
import CLabel from '../../../../../migration/CLabel';
import { CTextarea } from '../../../../../migration/CInput';
import { CInvalidFeedback } from '../../../../../migration/CInvalidFeedback';
import ToastEditor from '../../../../../general/toastEditor';

const FormGroup = React.memo(({ name, label, title, editState, errors, value, rows = 4, isEditor = false, disabled, onChange, setFieldValue,  ...props }) => {

     const getValue = () => {
          if ('mainInstructions' === name) {
               if (editState.mainInstructions) {
                    return value;
               }
          }
          return value;
     };
    
     return (
          <CFormGroup className="mb-3">
               {label && <CLabel htmlFor={name}>{label}</CLabel>}
               {isEditor ? (
                    <ToastEditor
                         id={name}
                         initialValue={value || ''}
                         disable={disabled}
                         onChange={(content) => setFieldValue(name, content)}
                    />
               ) : (
                    <CTextarea
                         id={name}
                         invalid={!!errors[name]}
                         value={getValue()}
                         className="py-2 px-3 lh-base form-textarea overflow-auto scrollbar-bar-custom"
                         rows={rows}
                         readOnly={disabled}
                         onChange={onChange}
                         {...props}
                    />
               )}
               <CInvalidFeedback>{errors[name]}</CInvalidFeedback>
          </CFormGroup>
     );
});

const InstructionsPreview = ({ values, errors, handleChange, setFieldValue }) => {
     const [editState, setEditState] = useState({
          guidanceOverride: false,
          mainInstructions: false,
     });

     const toggleEdit = useCallback((field) => {
          setEditState((prev) => ({ ...prev, [field]: !prev[field] }));
     }, []);

     const fields = useMemo(
          () => [
               { name: 'guidanceOverride', label: 'Guidance', isEditor: true },
               { name: 'mainInstructions', label: 'Main Instructions', isEditor: true },
               {
                    name: 'dataDescriptions',
                    label: 'Data Descriptions',
                    desc: 'Visit Step 2 to edit these details.',
                    isEditor: true,
                    readonly: true,
               },
               { name: 'exampleOutputs', label: 'Example Output', isEditor: true },
               {
                    name: 'context',
                    label: 'Context',
                    desc: 'Visit Step 3 to edit these details.',
                    isEditor: true,
                    readonly: true,
               },
               {
                    name: 'rulesAndFilters',
                    label: 'Rules/Filters',
                    desc: 'Visit Step 3 to edit these details.',
                    isEditor: true,
                    readonly: true,
               },
          ],
          []
     );

     const renderField = useCallback(
          (field) => {
               const { name, label, title, desc, isEditor, readonly } = field;
               const isEditable = ['guidanceOverride', 'mainInstructions'].includes(name);
               const isDisabled = readonly || (isEditable && !editState[name]);
              
               let exclude = false;

               if(name === 'rulesAndFilters'){
                    exclude = values.isRulesAndFiltersExcluded
               }
               
               if(name === 'context'){
                    exclude = values.isBusinessDescriptionExcluded && values.isCustomerProfileExcluded
               }
               if(name === 'exampleOutputs'){
                    exclude = values.isExcludeExampleOutput
               }
               
               if(exclude) return;
               return (
                    <CCol key={name} lg="12">
                         <div className="mb-2">
                              <h3 className="f-15 mb-0">{label}</h3>
                              {desc && <p className="text-muted f-13 mb-0">{desc}</p>}
                         </div>

                         <div className="position-relative">
                              {isEditable && (
                                   <div className="button-edit">
                                        {editState[name] ? (
                                             <CButton type="button" color="link" className="px-1" onClick={() => toggleEdit(name)}>
                                                  Done
                                             </CButton>
                                        ) : (
                                             <CButton type="button" color="link" className="px-2 py-1" onClick={() => toggleEdit(name)}>
                                                  <CIcon icon="icon-pencil" height={14} />
                                             </CButton>
                                        )}
                                   </div>
                              )}

                              <FormGroup
                                   name={name}
                                   label={null}
                                   title={title}
                                   value={values[name]}
                                   errors={errors}
                                   onChange={handleChange}
                                   setFieldValue={setFieldValue}
                                   placeholder={`Enter ${label}`}
                                   disabled={isDisabled}
                                   isEditor={isEditor}
                                   editState={editState}
                              />
                         </div>
                    </CCol>
               );
          },
          [editState, values, errors, handleChange, setFieldValue, toggleEdit]
     );

     return (
          <>
               <p>Full Instructions</p>
               <CCard className="mb-3 instructions-preview">
                    <CCardBody className="p-4">
                         <div className="mb-4">
                              <CRow className="gap-3">{fields.map(renderField)}</CRow>
                         </div>
                    </CCardBody>
               </CCard>
          </>
     );
};

InstructionsPreview.propTypes = {
     values: PropTypes.object.isRequired,
     errors: PropTypes.object.isRequired,
     handleChange: PropTypes.func.isRequired,
     setFieldValue: PropTypes.func.isRequired,
     stepsData: PropTypes.object.isRequired,
};

export default React.memo(InstructionsPreview);
